import type { TFunction } from "i18next";
import type { ListedConnector } from "../../../api/connectorsApi";
import type { OfferedEffort } from "../../../api/connectorStatus";
import type { CardOption } from "../../../ui/forms/OptionCard";

/**
 * Preferred reasoning levels per agent (Johannes, 3 Nov 16:51): the person's over the
 * installation's, for Runs left at "Standard". The agents and their levels are what the PCs
 * report at run time, never a list in code; which level applies is module C's (only where
 * the agent offers it).
 */

/** An agent the PCs report, with every reasoning level any of them offers for its default LLM. */
export interface AgentLevels {
  readonly agentId: string;
  readonly title: string;
  readonly efforts: readonly OfferedEffort[];
}

/** The agents on these PCs that offer reasoning levels, by agent ID, each level once. */
export function agentLevelsOf(connectors: readonly ListedConnector[]): AgentLevels[] {
  const byAgent = new Map<string, { title: string; efforts: Map<string, OfferedEffort> }>();
  for (const agent of connectors.flatMap((connector) => connector.status.agents)) {
    if (agent.reasoningEfforts.length === 0) continue;
    const entry = byAgent.get(agent.agentId) ?? { title: agent.title ?? agent.agentId, efforts: new Map<string, OfferedEffort>() };
    for (const effort of agent.reasoningEfforts) if (!entry.efforts.has(effort.effortId)) entry.efforts.set(effort.effortId, effort);
    byAgent.set(agent.agentId, entry);
  }
  return [...byAgent].map(([agentId, { title, efforts }]) => ({ agentId, title, efforts: [...efforts.values()] }));
}

/** "Standard" in a level choice: no level of its own, so the one beneath applies. */
export const STANDARD_LEVEL = "";

/**
 * One agent's level choice: "Standard", named by the level beneath it where the agent offers
 * that one, then each level it offers; a `chosen` level no PC offers now stays, by its ID.
 */
export function levelChoices(agent: AgentLevels, beneath: string | undefined, chosen: string | undefined, t: TFunction): CardOption<string>[] {
  const offered = (effortId: string | undefined) => agent.efforts.find((effort) => effort.effortId === effortId);
  const below = offered(beneath);
  const standard = below === undefined ? t("threads.llm.default") : t("threads.llm.defaultLevel", { level: below.displayName });
  const kept = chosen !== undefined && offered(chosen) === undefined ? [{ value: chosen, label: chosen }] : [];
  return [{ value: STANDARD_LEVEL, label: standard }, ...agent.efforts.map((effort) => ({ value: effort.effortId, label: effort.displayName })), ...kept];
}
